"use client";

function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { Footer as BumbleFooter } from "@bumble/ui-kit";
import { useRouter } from "next/navigation";
import { useEffect, type MouseEvent } from "react";
import locales from "../../../../locales.json";
import { useStore } from "@/lib/state";
import { addLocalePrefix } from "@/lib/utils/addLocalePrefix";
import { getManageCookiesLabel } from "@/components/CookieConsent/labels";
import { openPrivacyManager } from "@/components/CookieConsent/openPrivacyManager";
const languages = Object.entries(locales).map(([value, title]) => ({
  value,
  title
}));
interface FooterLink {
  id: string;
  text: string;
  url: string;
  hasIcon: boolean;
}
interface FooterLinkGroup {
  title: string;
  links: FooterLink[];
}
interface SocialLink {
  id: string;
  url: string;
  a11yLabel: string;
  iconName: string;
}
export interface FooterData {
  appLinks?: FooterLinkGroup | null;
  companyLinks?: FooterLinkGroup | null;
  legalLinks?: FooterLinkGroup | null;
  socialLinks: SocialLink[];
  downloadWidgetText?: string | null;
  downloadCtaText?: string | null;
  downloadUrl?: string | null;
  languageA11yLabel?: string | null;
}
interface Props {
  data: FooterData | null;
  currentLocale: string;
  availableLocales: string[];
}

// Transform DatoCMS link group to BumbleFooter format
const transformLinkGroup = (group: FooterLinkGroup | null | undefined, locale: string) => {
  if (!group) return {
    title: "",
    links: []
  };
  return {
    title: group.title,
    links: group.links.map(link => ({
      text: link.text,
      url: addLocalePrefix(link.url, locale),
      hasIcon: link.hasIcon
    }))
  };
};
export default function Footer({
  data,
  currentLocale,
  availableLocales
}: Props) {
  const router = useRouter();
  const setLanguage = useStore(state => state.setLanguage);

  // Sync state with current locale
  useEffect(() => {
    setLanguage(currentLocale);
  }, [currentLocale, setLanguage]);
  const handleLanguageChange = (lang: string) => {
    const targetLocale = availableLocales.includes(lang) ? lang : availableLocales[0];
    setLanguage(targetLocale);
    router.push(`/${targetLocale}`);
  };
  const socialLinks = data?.socialLinks.map(link => ({
    url: link.url,
    a11yLabel: link.a11yLabel,
    iconName: link.iconName
  })) ?? [];
  const legalLinks = transformLinkGroup(data?.legalLinks, currentLocale);
  // Locale-prefix the cookie-policy fallback so it matches the routing scheme of
  // every other footer link (/<locale>/cookie-policy), used both as the link
  // href and as openPrivacyManager's fallback when the CMP isn't available.
  // Built directly rather than via addLocalePrefix, whose "looks like a locale"
  // heuristic mangles hyphenated first segments (e.g. "cookie-policy").
  const cookiePolicyUrl = `/${currentLocale}/cookie-policy`;
  const legalLinksWithManageCookies = _objectSpread(_objectSpread({}, legalLinks), {}, {
    links: [...legalLinks.links, {
      text: getManageCookiesLabel(currentLocale),
      url: cookiePolicyUrl,
      hasIcon: false,
      onClick: (event: MouseEvent<HTMLAnchorElement>) => openPrivacyManager(event, cookiePolicyUrl)
    }]
  });
  return <BumbleFooter appLinks={transformLinkGroup(data?.appLinks, currentLocale)} companyLinks={transformLinkGroup(data?.companyLinks, currentLocale)} legalLinks={legalLinksWithManageCookies} socialLinks={socialLinks} downloadWidgetText={data?.downloadWidgetText ?? "Download our app"} downloadCtaText={data?.downloadCtaText ?? "Download the app"} downloadUrl={data?.downloadUrl ?? "https://www.bumble.com/get-the-app"} languages={languages} lang={currentLocale} onLanguageChange={handleLanguageChange} languageA11yLabel={data?.languageA11yLabel ?? "Choose your language"} />;
}